/**
 * Staff — Training Course detail. Shows the register (add / remove trainees),
 * the confirmed-attendance status, the QR code trainees scan on arrival, and
 * a bulk-paste box for populating the register from a spreadsheet.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Plus, Trash2, GraduationCap, QrCode, Download, Upload, MessageSquare, Loader2, Printer, Calendar, Clock, Pencil,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { paletteForCourse, accentForCourse } from '@/pages/trainingBrand';
import { defaultCourseFeeForSections } from '@/pages/trainingFees';
import useTrainingSettings from '@/hooks/useTrainingSettings';
import logger from '@/utils/logger';
import CourseSmsBlastModal from '@/components/training/CourseSmsBlastModal';
import ChangeCourseDateDialog from '@/components/training/ChangeCourseDateDialog';
import EditCourseDialog from '@/components/training/EditCourseDialog';
import MoveTraineesModal from '@/components/training/MoveTraineesModal';
import SpreadsheetImportModal from '@/components/training/SpreadsheetImportModal';
import { looksLikeBookingSheet } from '@/components/training/spreadsheetImport';

import { SECTION_LABEL, PAYMENT_METHOD_LABEL } from '@/components/training/paymentHistory';
import { EditPaymentsDialog, RecordPaymentDialog } from '@/components/training/PaymentDialogs';
import { AddTraineeDialog } from '@/components/training/AddTraineeDialog';
import { CourseRegister } from '@/components/training/CourseRegister';

export default function TrainingCourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { settings } = useTrainingSettings();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ course: null, registrations: [] });
  const [addingOne, setAddingOne] = useState(false);
  // Default course fee comes from the loaded course's sections — ProBound
  // £534, ProEpoxy £894, ProDecor £630 (all VAT-inc). Combo courses sum
  // the sections. Falls back to a blank field when the course has no
  // known sections so staff aren't confused by an arbitrary number.
  const defaultFee = defaultCourseFeeForSections(data?.course?.sections, settings);
  const defaultFeeStr = defaultFee !== null ? String(defaultFee) : '';
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    payment_total: '', payment_received: '',
    notes: '', hear_about: '',
    // Multi-course booking state — the current course is always ticked,
    // additional courses come from `otherCourses` state below.
    extra_course_ids: [],
    apply_multi_discount: false,
    free_city_guilds: false,
  });
  // Other upcoming courses (excluding the current one) so staff can tick
  // extras to book the same trainee onto more than one date at once.
  const [otherCourses, setOtherCourses] = useState([]);
  // Track which payment-history entry is mid-delete so the trash button
  // shows a spinner + disables to prevent double taps. Keyed by
  // `${registrationId}:${originalIndex}` — a compound key because a
  // popover could be open per row.
  const [deletingPayment, setDeletingPayment] = useState(null);
  // Register filter — 'all' | 'paid' | 'owed'. Guests always follow their
  // primary trainee so a filter on the primary shows/hides its guests too.
  const [balanceFilter, setBalanceFilter] = useState('all');

  // When the course loads (or its sections change), seed the form with
  // the brand-appropriate default. Only writes when the field is empty
  // so a mid-typing staff member doesn't lose what they've entered.
  useEffect(() => {
    if (defaultFeeStr && !form.payment_total) {
      setForm((f) => (f.payment_total ? f : { ...f, payment_total: defaultFeeStr }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFeeStr]);
  const [duplicateInfo, setDuplicateInfo] = useState(null);  // set when the typed email already has an account
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sheetImport, setSheetImport] = useState(null); // { raw } when the spreadsheet importer is open
  const openSheetImporter = (raw = '') => { setBulkOpen(false); setSheetImport({ raw }); };
  const [bulkText, setBulkText] = useState('');
  const bulkLooksLikeSheet = looksLikeBookingSheet(bulkText);
  const [qrOpen, setQrOpen] = useState(false);
  const [smsBlastOpen, setSmsBlastOpen] = useState(false);
  const [deleteReg, setDeleteReg] = useState(null);
  // Bulk selection — set of registration ids, mirrors backend
  // `bulk-delete` / `move` payload shape.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [moveReg, setMoveReg] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editReg, setEditReg] = useState(null);           // registration currently being edited (payments)
  const [editForm, setEditForm] = useState({ payment_total: '', payment_received: '' });
  const [paymentReg, setPaymentReg] = useState(null);     // registration recording a live payment
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: '' });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [attendanceBusyIds, setAttendanceBusyIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load course');
      setData(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    fetchCourse();
  }, [user, courseId, fetchCourse, navigate]);

  /**
   * Delete a single entry from a trainee's payment_history. Confirms
   * first because this recalculates the paid total (removing a bogus
   * £708 entry drops payment_received by £708 and reopens the balance).
   * The backend rebuilds `payment_received` + `balance_owed` server-side
   * so we don't need to guess client-side.
   */
  const deletePaymentEntry = useCallback(async (registrationId, originalIndex, entry) => {
    const label = `£${Number(entry?.amount || 0).toFixed(2)}${entry?.method ? ` · ${entry.method}` : ''}`;
    if (!window.confirm(
      `Remove this payment (${label})?\n\n` +
      `The trainee's paid total will drop by £${Number(entry?.amount || 0).toFixed(2)}. ` +
      `If that reopens their balance, "DUE TODAY" will come back on the register.`
    )) return;
    setDeletingPayment(`${registrationId}:${originalIndex}`);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${registrationId}/payment-history/${originalIndex}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed');
      }
      const out = await res.json();
      toast.success(`Removed £${Number(out.removed_amount || 0).toFixed(2)} payment`);
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingPayment(null);
    }
  }, [courseId, fetchCourse]);

  /**
   * Edit a single entry — sends the {amount, method, at} patch to the
   * backend, which recomputes payment_received + balance_owed and
   * stamps/clears balance_paid_at as needed. Same registration-index
   * key pattern as delete so both actions can't collide.
   */
  const editPaymentEntry = useCallback(async (registrationId, originalIndex, patch) => {
    setDeletingPayment(`edit:${registrationId}:${originalIndex}`);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${registrationId}/payment-history/${originalIndex}`,
        {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Update failed');
      }
      toast.success('Payment updated');
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingPayment(null);
    }
  }, [courseId, fetchCourse]);

  /**
   * Log a fresh payment on a trainee's register. Same recompute
   * contract as edit/delete — backend appends the entry then
   * re-derives payment_received/balance_owed/balance_paid_at.
   */
  const addPaymentEntry = useCallback(async (registrationId, payload) => {
    setDeletingPayment(`add:${registrationId}`);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${registrationId}/payment-history`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Add failed');
      }
      const out = await res.json();
      const bal = out.registration?.balance_owed;
      const word = payload.kind === 'refund' ? 'Refund recorded' : 'Payment logged';
      if (bal === 0) {
        toast.success(`${word} · Balance now PAID in full`);
      } else if (bal > 0) {
        toast.success(`${word} · £${bal.toFixed(2)} still to collect`);
      } else {
        toast.success(word);
      }
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingPayment(null);
    }
  }, [courseId, fetchCourse]);

  // Deep link from Training Enquiries → "Book onto course": open the Add
  // Trainee modal pre-filled, remember the enquiry so we can flip it to
  // Booked once the trainee is saved.
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromEnquiry, setFromEnquiry] = useState(null);
  useEffect(() => {
    if (searchParams.get('addTrainee') !== '1') return;
    setForm((f) => ({
      ...f,
      name: searchParams.get('name') || f.name,
      email: searchParams.get('email') || f.email,
      phone: searchParams.get('phone') || f.phone,
    }));
    setFromEnquiry(searchParams.get('enquiry') || null);
    setAddingOne(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const markEnquiryBooked = useCallback(async (registrationId) => {
    if (!fromEnquiry) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${fromEnquiry}/booked`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId, registration_id: registrationId || null }),
      });
      if (res.ok) toast.success('Enquiry marked as Booked');
    } catch (err) {
      logger.warn('[training] could not mark enquiry booked:', err);
    } finally {
      setFromEnquiry(null);
    }
  }, [fromEnquiry, courseId]);

  // When the Add Trainee modal opens, load the other courses so staff can
  // tick extras. Skip if already fetched (courses change infrequently and
  // the user can close/re-open the modal without a network roundtrip).
  useEffect(() => {
    if (!addingOne || otherCourses.length > 0) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include' });
        if (!res.ok) return;
        const all = await res.json();
        const todayIso = new Date().toISOString().slice(0, 10);
        const upcoming = all.filter(c => c.id !== courseId && (!c.date || c.date >= todayIso));
        setOtherCourses(upcoming);
      } catch (err) {
        logger.warn('[training] failed to load other courses:', err);
      }
    })();
  }, [addingOne, courseId, otherCourses.length]);

  const addOne = async ({ skipDuplicateCheck = false } = {}) => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required.');
      return;
    }

    // Deposit is required — the check-in person shouldn't add someone
    // without recording what they've paid. `0` is allowed (some bookings
    // are free of charge) but an empty string is not.
    if (form.payment_received === '' || form.payment_received === null || form.payment_received === undefined) {
      toast.error('Enter a deposit amount (use 0 for free courses).');
      return;
    }
    const depositNum = Number(form.payment_received);
    if (!Number.isFinite(depositNum) || depositNum < 0) {
      toast.error('Deposit must be a positive number.');
      return;
    }

    // Quick client-side email sanity check so we don't ping the lookup
    // endpoint with an obvious typo.
    const normalisedEmail = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalisedEmail)) {
      toast.error('That doesn\'t look like a valid email address. Check for a missing @ or . ');
      return;
    }

    // Duplicate-email check: if this email already has an Ox Resin account,
    // pop a confirmation dialog with the person's history first. Staff can
    // then proceed (adds as a repeat booking — brand access is unioned onto
    // the existing account) or cancel.
    if (!skipDuplicateCheck) {
      setBusy(true);
      try {
        const lookup = await fetch(
          `${API_URL}/api/staff/trainees/lookup?email=${encodeURIComponent(normalisedEmail)}`,
          { credentials: 'include' },
        );
        if (lookup.ok) {
          const data = await lookup.json();
          if (data.exists) {
            setDuplicateInfo(data);
            setBusy(false);
            return;
          }
        }
      } catch (err) {
        // Fail-open — the create endpoint will still catch dupes server-side.
        // Log so devtools shows the network issue if this ever fires unexpectedly.
        logger.warn('[training] duplicate-email lookup failed, continuing:', err);
      }
    }

    setBusy(true);
    try {
      // Multi-course branch: extras ticked → /multi-book (splits fee +
      // discount across courses server-side, all sharing a booking_group).
      if (form.extra_course_ids.length > 0) {
        const body = {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          course_ids: [courseId, ...form.extra_course_ids],
          apply_multi_course_discount: !!form.apply_multi_discount,
          free_city_guilds: !!form.free_city_guilds,
        };
        if (form.payment_received !== '') body.deposit_total = Number(form.payment_received);
        if (form.notes && form.notes.trim()) body.notes = form.notes.trim();
        if (form.hear_about) body.hear_about = form.hear_about;
        const res = await fetch(`${API_URL}/api/staff/training-courses/multi-book`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(formatApiError(err) || 'Multi-course booking failed');
        }
        const result = await res.json();
        await markEnquiryBooked(result.registration_ids?.[0] || null);
        toast.success(
          `${form.name} booked onto ${result.created_count} course${result.created_count === 1 ? '' : 's'}` +
          (result.discount_amount > 0 ? ` · £${result.discount_amount.toFixed(2)} discount applied` : '')
        );
        setAddingOne(false);
        setDuplicateInfo(null);
        setForm({
          name: '', email: '', phone: '',
          payment_total: defaultFeeStr, payment_received: '',
          notes: '',
          extra_course_ids: [],
          apply_multi_discount: false,
          free_city_guilds: false,
        });
        fetchCourse();
        return;
      }
      const body = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        free_city_guilds: !!form.free_city_guilds,
      };
      if (form.payment_total !== '') body.payment_total = Number(form.payment_total);
      if (form.payment_received !== '') body.payment_received = Number(form.payment_received);
      if (form.notes && form.notes.trim()) body.notes = form.notes.trim();
      if (form.hear_about) body.hear_about = form.hear_about;
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to add trainee');
      }
      const created = await res.json().catch(() => ({}));
      await markEnquiryBooked(created.id || null);
      const emailOk = created.welcome_sent_at;
      if (emailOk) {
        toast.success(`${form.name} added — welcome emails sent`);
      } else {
        toast.warning(`${form.name} added but welcome emails failed to send. Use "Re-send" once the email provider is fixed.`);
      }
      setAddingOne(false);
      setDuplicateInfo(null);
      setForm({
        name: '', email: '', phone: '',
        payment_total: defaultFeeStr, payment_received: '',
        notes: '',
        extra_course_ids: [],
        apply_multi_discount: false,
        free_city_guilds: false,
      });
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const parseBulk = () => {
    // Accept CSV, tab-separated, or one-per-line:
    //   "name, email, phone, payment_total, payment_received"
    // phone / totals are all optional.
    const lines = bulkText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const trainees = [];
    for (const line of lines) {
      const parts = line.split(/,|\t/).map(p => p.trim());
      const [name, email, phone, total, received] = parts;
      if (name && email) {
        const item = { name, email, phone: phone || null };
        const tot = parseFloat(String(total || '').replace(/[£\s]/g, ''));
        const rec = parseFloat(String(received || '').replace(/[£\s]/g, ''));
        if (!Number.isNaN(tot) && tot >= 0) item.payment_total = tot;
        if (!Number.isNaN(rec) && rec >= 0) item.payment_received = rec;
        trainees.push(item);
      }
    }
    return trainees;
  };

  const saveTraineeEdit = async () => {
    if (!editReg) return;
    setBusy(true);
    try {
      const body = {};
      if (editForm.payment_total !== '') body.payment_total = Number(editForm.payment_total);
      if (editForm.payment_received !== '') body.payment_received = Number(editForm.payment_received);
      if (Object.keys(body).length === 0) {
        // Nothing typed — treat as a clear (set both to 0)
        body.payment_total = 0;
        body.payment_received = 0;
      }
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/${editReg.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to update payments');
      }
      toast.success('Payments updated');
      setEditReg(null);
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resendWelcome = async (reg) => {
    setResendingId(reg.id);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${reg.id}/send-welcome`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to send welcome emails');
      }
      toast.success(`Welcome emails re-sent to ${reg.email}`);
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResendingId(null);
    }
  };

  const openPaymentModal = (reg) => {
    // Pre-fill the amount input with the outstanding balance so a "the
    // trainee just paid off their balance" case is a single tap.
    const outstanding = Number(reg.balance_owed || 0);
    setPaymentForm({
      amount: outstanding > 0 ? outstanding.toFixed(2) : '',
      method: '',
    });
    setPaymentReg(reg);
  };

  const recordPayment = async () => {
    if (!paymentReg) return;
    const amount = Number(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter an amount greater than 0.');
      return;
    }
    if (!paymentForm.method) {
      toast.error('Choose how the trainee paid.');
      return;
    }
    setPaymentBusy(true);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${paymentReg.id}/payments`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, method: paymentForm.method }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to record payment');
      }
      toast.success(`Recorded £${amount.toFixed(2)} · ${PAYMENT_METHOD_LABEL[paymentForm.method]}`);
      setPaymentReg(null);
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPaymentBusy(false);
    }
  };

  const toggleAttendance = async (reg) => {
    // Manual staff toggle — the tap-the-circle fallback for trainees who
    // can't scan the QR (dead phone, walk-in, etc.). Untapping is safe:
    // sections stay granted so brand access isn't yanked on a fat-finger.
    if (attendanceBusyIds.has(reg.id)) return;
    setAttendanceBusyIds(prev => new Set(prev).add(reg.id));
    // Optimistic update — flip the flag immediately, roll back on error.
    const wasConfirmed = !!reg.confirmed_at;
    setData(d => ({
      ...d,
      registrations: d.registrations.map(x =>
        x.id === reg.id
          ? { ...x, confirmed_at: wasConfirmed ? null : new Date().toISOString() }
          : x
      ),
    }));
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${courseId}/registrations/${reg.id}/attendance`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to update attendance');
      }
      toast.success(wasConfirmed ? `${reg.name} marked absent` : `${reg.name} marked present`);
      // Refresh so the server timestamp (and any user_id linkage) reflects reality.
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
      // Roll back the optimistic flip.
      setData(d => ({
        ...d,
        registrations: d.registrations.map(x =>
          x.id === reg.id ? { ...x, confirmed_at: reg.confirmed_at } : x
        ),
      }));
    } finally {
      setAttendanceBusyIds(prev => {
        const next = new Set(prev);
        next.delete(reg.id);
        return next;
      });
    }
  };

  const submitBulk = async () => {
    if (bulkLooksLikeSheet) { openSheetImporter(bulkText); return; }
    const trainees = parseBulk();
    if (trainees.length === 0) {
      toast.error('No valid rows found. Format: name, email, phone (one per line).');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainees }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Bulk add failed');
      }
      const { added, skipped } = await res.json();
      toast.success(`${added} added${skipped ? `, ${skipped} skipped (duplicates)` : ''}`);
      setBulkOpen(false);
      setBulkText('');
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteReg) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/${deleteReg.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to remove');
      toast.success(`${deleteReg.name} removed`);
      setDeleteReg(null);
      fetchCourse();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const stats = useMemo(() => {
    const total = data.registrations.length;
    const confirmed = data.registrations.filter(r => r.confirmed_at).length;
    // Compute the "owing today" total using the same precedence as
    // `resolveBalance` below so the header stat and the per-row chip
    // never disagree:
    //   1. `balance_paid_at` stamped → row is paid, skip.
    //   2. `balance_owed > 0` → use it.
    //   3. Structured `balance_owed === 0` but no `balance_paid_at` → this
    //      is ambiguous. Legacy imports done before the payment_history
    //      fix stored 0 as a default. Fall through to
    //      `booking_details.final_balance` (col I — CURRENT) which is
    //      the trainee's TRUE outstanding. Prefer it over
    //      `balance_on_booking` (col G — HISTORICAL, will be wrong after
    //      any BT/Worldpay payment has arrived post-booking).
    //   4. Otherwise treat as paid.
    const rowOwed = (r) => {
      if (r.balance_paid_at) return 0;
      const structured = Number(r.balance_owed || 0);
      if (structured > 0) return structured;
      const bd = r.booking_details || {};
      const finalRaw = String(bd.final_balance ?? '').trim();
      const bookingRaw = String(bd.balance_on_booking ?? '').trim();
      const raw = finalRaw !== '' ? finalRaw : bookingRaw;
      if (raw === '') return 0;
      const imported = parseFloat(raw.replace(/[£,\s]/g, ''));
      return Number.isFinite(imported) && imported > 0 ? imported : 0;
    };
    const owed = data.registrations.reduce((sum, r) => sum + rowOwed(r), 0);
    // Money view is per booking (primaries) — +1 seats carry no fee of their own.
    const primaries = data.registrations.filter((r) => !r.is_guest);
    const owing = primaries.filter((r) => rowOwed(r) > 0).length;
    const paidInFull = primaries.length - owing;
    return { total, bookings: primaries.length, confirmed, pending: total - confirmed, owed, owing, paidInFull };
  }, [data.registrations]);

  /**
   * Resolve the "amount owing on the day" for a single trainee row.
   * Prefers structured payment fields, then the spreadsheet-imported
   * text field, then returns 0. Returned amount is a positive number
   * (never a negative). `paid` = true when there's simply nothing to
   * collect (either paid_at flag set or resolved balance is £0).
   */
  const resolveBalance = (r) => {
    if (r.balance_paid_at) return { amount: 0, paid: true, hasData: true };
    const structured = Number(r.balance_owed || 0);
    if (structured > 0) return { amount: structured, paid: false, hasData: true };
    // Structured balance_owed is 0 (or missing) AND no balance_paid_at —
    // this row could be a legacy import stored with owed=0 as a default.
    // Fall through to the booking_details columns to check what the
    // spreadsheet said. Prefer col I (Final Balance = CURRENT) over col
    // G (Balance on booking = HISTORICAL).
    const bd = r.booking_details || {};
    const finalRaw = String(bd.final_balance ?? '').trim();
    const bookingRaw = String(bd.balance_on_booking ?? '').trim();
    const importedRaw = finalRaw !== '' ? finalRaw : bookingRaw;
    if (importedRaw !== '') {
      const parsed = parseFloat(importedRaw.replace(/[£,\s]/g, ''));
      if (Number.isFinite(parsed)) {
        return { amount: Math.max(0, parsed), paid: parsed <= 0, hasData: true };
      }
    }
    if ((r.payment_received || 0) > 0 || (r.payment_total || 0) > 0) {
      return { amount: 0, paid: true, hasData: true };
    }
    return { amount: 0, paid: false, hasData: false };
  };

  const qrImageUrl = `${API_URL}/api/staff/training-courses/${courseId}/qr`;
  // Public trainee URL — used for the copy-link button + QR preview info.
  const publicUrl = data.course
    ? `${window.location.origin}/training/register/${courseId}?token=${data.course.qr_token || ''}`
    : '';

  const palette = paletteForCourse(data.course);

  if (loading || !data.course) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${palette.tint} 0%, #FFFFFF 350px)` }}>
      <div className="border-b sticky top-0 z-20 shadow-sm" style={{ background: palette.primary, color: palette.ink }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/staff/training-courses" style={{ color: palette.ink, opacity: 0.85 }} data-testid="training-course-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" style={{ color: palette.ink, opacity: 0.85 }}>
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)', color: palette.ink }}>
            <GraduationCap className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold truncate flex items-center gap-2" style={{ color: palette.ink }}>{data.course.name}
                <button type="button" onClick={() => setEditOpen(true)} className="text-xs font-semibold px-2 py-0.5 rounded-md inline-flex items-center gap-1 hover:bg-white/20" style={{ color: palette.ink, border: '1px solid rgba(255,255,255,0.4)' }} title="Edit course" data-testid="training-course-edit-btn"><Pencil className="w-3 h-3" /> Edit</button>
              </h1>
              {data.course.date && (
                <button
                  type="button"
                  onClick={() => setDateOpen(true)}
                  title="Change the course date"
                  className="inline-flex items-center gap-1 text-sm font-bold px-2.5 py-0.5 rounded-lg whitespace-nowrap hover:brightness-95"
                  style={{ background: accentForCourse(data.course).primary, color: accentForCourse(data.course).ink, boxShadow: '0 0 0 2px rgba(255,255,255,0.85)' }}
                  data-testid="training-course-header-date"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(`${data.course.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  <span className="text-[10px] font-semibold opacity-75 ml-1">change</span>
                </button>
              )}
              <StartTimeChip course={data.course} courseId={courseId} onSaved={fetchCourse} palette={palette} />
              {data.course.reference && (
                <span
                  className="text-[11px] font-mono px-2 py-0.5 rounded uppercase tracking-wider"
                  style={{ background: 'rgba(255,255,255,0.2)', color: palette.ink }}
                  data-testid="training-course-header-ref"
                >
                  {data.course.reference}
                </span>
              )}
            </div>
            <p className="text-xs" style={{ color: palette.ink, opacity: 0.75 }}>{data.course.date} · Grants: {(data.course.sections || []).map(s => SECTION_LABEL[s] || s).join(', ')}</p>
          </div>
          <Button
            className="ml-auto bg-white font-bold hover:bg-white/90"
            style={{ color: palette.dark }}
            onClick={() => setQrOpen(true)}
            data-testid="training-course-qr-btn"
          >
            <QrCode className="w-4 h-4 mr-1.5" /> Show QR
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Money summary tiles — attendance ticks live on the rows for the day itself */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Booked</div>
            <div className="text-2xl font-bold text-gray-900" data-testid="stat-registered">
              {stats.total} <span className="text-sm font-semibold text-gray-500">seat{stats.total === 1 ? '' : 's'}</span>
            </div>
            {stats.total !== stats.bookings && (
              <div className="text-xs text-gray-500" data-testid="stat-bookings">{stats.bookings} booking{stats.bookings === 1 ? '' : 's'} · {stats.total - stats.bookings} +1</div>
            )}
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Paid in full</div>
            <div className="text-2xl font-bold text-emerald-700" data-testid="stat-paid-in-full">{stats.paidInFull}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Balance remaining</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="stat-owing">{stats.owing} <span className="text-sm font-semibold text-amber-700/70">{stats.owing === 1 ? 'person' : 'people'}</span></div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Balance owed</div>
            <div className="text-2xl font-bold text-rose-700" data-testid="stat-balance-owed">£{stats.owed.toFixed(2)}</div>
          </Card>
        </div>

        {/* Action row */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            className="text-white font-bold"
            style={{ background: palette.primary }}
            onClick={() => setAddingOne(true)}
            data-testid="training-add-one-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add trainee
          </Button>
          <Button
            variant="outline"
            onClick={() => setSmsBlastOpen(true)}
            data-testid="training-sms-blast-btn"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <MessageSquare className="w-4 h-4 mr-1.5" /> SMS text
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/staff/training-courses/${courseId}/print`, '_blank')}
            data-testid="training-print-register-btn"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
            title="Open a printable A4 register for the door person"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print register
          </Button>
          <Button variant="outline" onClick={() => openSheetImporter('')} data-testid="training-sheet-import-btn" title="Paste the course block from your booking spreadsheet">
            <Upload className="w-4 h-4 mr-1.5" /> Import spreadsheet
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="training-bulk-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Bulk add (CSV)
          </Button>
        </div>

        {/* Register */}
        <CourseRegister addPaymentEntry={addPaymentEntry} attendanceBusyIds={attendanceBusyIds} balanceFilter={balanceFilter} courseId={courseId} data={data} deletePaymentEntry={deletePaymentEntry} deletingPayment={deletingPayment} editPaymentEntry={editPaymentEntry} fetchCourse={fetchCourse} openPaymentModal={openPaymentModal} resendWelcome={resendWelcome} resendingId={resendingId} resolveBalance={resolveBalance} selectedIds={selectedIds} setBalanceFilter={setBalanceFilter} setBulkDeleteOpen={setBulkDeleteOpen} setDeleteReg={setDeleteReg} setEditForm={setEditForm} setEditReg={setEditReg} setMoveOpen={setMoveOpen} setMoveReg={setMoveReg} setSelectedIds={setSelectedIds} toggleAttendance={toggleAttendance} />
      </div>

      <AddTraineeDialog addOne={addOne} addingOne={addingOne} busy={busy} data={data} defaultFeeStr={defaultFeeStr} duplicateInfo={duplicateInfo} form={form} otherCourses={otherCourses} palette={palette} setAddingOne={setAddingOne} setDuplicateInfo={setDuplicateInfo} setForm={setForm} settings={settings} />

      {/* Bulk add modal */}
      <Dialog open={bulkOpen} onOpenChange={(o) => !busy && setBulkOpen(o)}>
        <DialogContent
          data-testid="training-bulk-modal"
          className="!bg-white"
          style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: palette.dark }}>Bulk add trainees</DialogTitle>
            <DialogDescription>
              Paste one trainee per line as <code>name, email, phone, course fee, paid so far</code>. Phone and payments are optional. Commas or tabs both work. Duplicates are skipped. <b>Welcome emails fire automatically</b> for every row added.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={10}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            data-testid="training-bulk-textarea"
            placeholder={`Jane Smith, jane@example.com, 07700 900001, 750, 250\nJohn Doe, john@example.com, , 750, 750`}
            className="font-mono text-sm bg-white"
          />
          {bulkLooksLikeSheet && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="training-bulk-sheet-hint">
              This looks like your <strong>booking spreadsheet</strong> (name · phone · email · deposit…), not the simple CSV. Use the spreadsheet importer so phones, deposits, balances and +1 guests all land correctly — and no welcome emails go out.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitBulk} disabled={busy} data-testid="training-bulk-submit" className="text-white font-bold" style={{ background: palette.primary }}>
              {busy ? 'Adding…' : bulkLooksLikeSheet ? 'Open spreadsheet importer' : `Add ${parseBulk().length || ''} trainees`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sheetImport && data.course && (
        <SpreadsheetImportModal
          open
          onClose={() => setSheetImport(null)}
          onImported={() => { setSheetImport(null); setBulkText(''); fetchCourse(); }}
          fixedCourse={{ id: data.course.id, name: data.course.name }}
          initialRaw={sheetImport.raw}
        />
      )}

      {/* QR modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent
          data-testid="training-qr-modal"
          className="max-w-md !bg-white"
          style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: palette.dark }}>Attendance QR code</DialogTitle>
            <DialogDescription>
              Show this QR at the venue. Trainees scan it, find their name, and confirm attendance.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3">
            <img
              src={qrImageUrl}
              alt="Training register QR code"
              className="w-64 h-64 border rounded bg-white"
              data-testid="training-qr-image"
            />
            <div className="text-xs text-gray-500 break-all text-center">{publicUrl}</div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl);
                  toast.success('Link copied');
                }}
                data-testid="training-qr-copy"
              >
                Copy link
              </Button>
              <a href={qrImageUrl} download={`${data.course.id}-qr.png`}>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-1.5" /> Download PNG
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EditPaymentsDialog editReg={editReg} setEditReg={setEditReg} editForm={editForm} setEditForm={setEditForm} busy={busy} saveTraineeEdit={saveTraineeEdit} palette={palette} />
      <RecordPaymentDialog paymentReg={paymentReg} setPaymentReg={setPaymentReg} paymentForm={paymentForm} setPaymentForm={setPaymentForm} paymentBusy={paymentBusy} recordPayment={recordPayment} palette={palette} />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteReg} onOpenChange={(o) => !o && setDeleteReg(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from register?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteReg?.name} will be removed from this course&apos;s register. Any existing user account they have on Ox Resin is <b>not</b> touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="training-delete-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditCourseDialog course={data?.course} open={editOpen} onClose={() => setEditOpen(false)} onSaved={fetchCourse} onChangeDate={() => setDateOpen(true)} />
      <ChangeCourseDateDialog course={data?.course} traineeCount={data?.registrations?.length || 0} open={dateOpen} onClose={() => setDateOpen(false)} onChanged={fetchCourse} />
      <CourseSmsBlastModal
        open={smsBlastOpen}
        onClose={() => setSmsBlastOpen(false)}
        courseId={courseId}
        courseSummary={data.course}
      />

      <MoveTraineesModal
        open={moveOpen || !!moveReg}
        onClose={() => { setMoveOpen(false); setMoveReg(null); }}
        sourceCourseId={courseId}
        sourceCourseName={data.course?.name}
        sourceCourseDate={data.course?.date}
        registrationIds={moveReg
          ? [moveReg.id, ...(data.registrations || []).filter((r) => r.guest_of_registration_id === moveReg.id).map((r) => r.id)]
          : Array.from(selectedIds)}
        traineeName={moveReg?.name}
        onMoved={() => {
          setSelectedIds(new Set());
          setMoveReg(null);
          fetchCourse();
        }}
      />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => { if (!bulkBusy) setBulkDeleteOpen(o); }}>
        <AlertDialogContent data-testid="register-bulk-delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">
              Delete {selectedIds.size} trainee{selectedIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes {selectedIds.size === 1 ? 'this trainee' : 'these trainees'} from
              the register. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-500 text-white"
              disabled={bulkBusy}
              data-testid="register-bulk-delete-confirm"
              onClick={async (e) => {
                e.preventDefault();
                setBulkBusy(true);
                try {
                  const r = await fetch(
                    `${API_URL}/api/staff/training-courses/${courseId}/registrations/bulk-delete`,
                    {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ registration_ids: Array.from(selectedIds) }),
                    },
                  );
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    throw new Error(err.detail || `HTTP ${r.status}`);
                  }
                  const data = await r.json();
                  toast.success(`Deleted ${data.deleted} trainee${data.deleted === 1 ? '' : 's'}`);
                  setSelectedIds(new Set());
                  setBulkDeleteOpen(false);
                  fetchCourse();
                } catch (err) {
                  toast.error('Bulk delete failed', { description: String(err.message || err).slice(0, 200) });
                } finally {
                  setBulkBusy(false);
                }
              }}
            >
              {bulkBusy ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Deleting…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-1.5" />Delete {selectedIds.size}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function StartTimeChip({ course, courseId, onSaved, palette }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(course.start_time || '');
  const fmt = (t) => { if (!t) return ''; const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`; };
  const save = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ start_time: val }) });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to save');
      toast.success(val ? `Start time ${fmt(val)} — used in this course's texts and emails` : 'Start time cleared — brand default will be used');
      setEditing(false); onSaved?.();
    } catch (e) { toast.error(e.message); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid="course-start-time-editor">
        <input type="time" value={val} onChange={(e) => setVal(e.target.value)} className="h-7 text-sm rounded px-1 text-gray-900" data-testid="course-start-time-input" autoFocus />
        <button type="button" onClick={save} className="text-xs font-bold px-2 py-1 rounded bg-white/90 text-gray-900" data-testid="course-start-time-save">Save</button>
        <button type="button" onClick={() => { setEditing(false); setVal(course.start_time || ''); }} className="text-xs px-1.5 py-1 rounded bg-white/30" style={{ color: palette.ink }}>Cancel</button>
      </span>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} title="Start time used in this course's texts and emails — click to change"
      className="inline-flex items-center gap-1 text-sm font-semibold px-2.5 py-0.5 rounded-lg whitespace-nowrap hover:bg-white/30"
      style={{ background: 'rgba(255,255,255,0.2)', color: palette.ink }} data-testid="course-start-time">
      <Clock className="w-3.5 h-3.5" /> {course.start_time ? `Starts ${fmt(course.start_time)}` : 'Set start time'}
    </button>
  );
}
